import { useEffect, useRef, useState } from "react";

import { AlertTriangle, CornerDownLeft, Loader2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MarkdownView } from "@/lib/markdown";
import { cn } from "@/lib/utils";

import type { CodeRagSearch } from "..";
import { useRagChat, useRagModels, type ChatMessage, type SearchHit } from "../api";
import { HitCard } from "./HitCard";
import { FilterSelects } from "./FilterSelects";

interface Turn extends ChatMessage {
  citations?: SearchHit[];
  model?: string;
}

export function AskTab({
  search,
  setSearch,
}: {
  search: CodeRagSearch;
  setSearch: (next: Partial<CodeRagSearch>) => void;
}) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [modelId, setModelId] = useState<string | undefined>(undefined);
  const chat = useRagChat();
  const { data: modelData } = useRagModels();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, chat.isPending]);

  const models = modelData?.models ?? [];
  const activeModel = modelId ?? modelData?.default_id ?? models[0]?.id;

  const send = () => {
    const content = draft.trim();
    if (!content || chat.isPending) return;

    // Send the history as it was *before* this turn plus the new question; the
    // assistant reply is appended when it lands. Citations are per-turn, so a
    // long conversation keeps each answer's sources next to it.
    const history: ChatMessage[] = [
      ...turns.map((t) => ({ role: t.role, content: t.content })),
      { role: "user" as const, content },
    ];
    setTurns((prev) => [...prev, { role: "user", content }]);
    setDraft("");

    chat.mutate(
      {
        messages: history,
        repo: search.repo,
        language: search.lang,
        model_id: activeModel,
        context_chunks: 8,
      },
      {
        onSuccess: (res) =>
          setTurns((prev) => [
            ...prev,
            {
              role: "assistant",
              content: res.answer,
              citations: res.citations,
              model: res.model,
            },
          ]),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelects
          repo={search.repo}
          lang={search.lang}
          onChange={(next) => setSearch(next)}
          disabled={chat.isPending}
        />
        <Select value={activeModel} onValueChange={setModelId} disabled={chat.isPending}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Model" />
          </SelectTrigger>
          <SelectContent>
            {models.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {turns.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={() => setTurns([])}>
            Clear conversation
          </Button>
        ) : null}
      </div>

      {turns.length === 0 && !chat.isPending ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Ask a question about the codebase. Every answer is grounded in chunks
            retrieved from the index and cites the files it used — so you can check
            the source rather than trust the summary.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            The index is a point-in-time snapshot. Check the Overview tab for how
            current each repo is before relying on an answer about recent work.
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-4">
        {turns.map((turn, i) => (
          <div
            key={i}
            className={cn("flex", turn.role === "user" ? "justify-end" : "justify-start")}
          >
            <div className={cn("max-w-3xl", turn.role === "user" && "max-w-xl")}>
              {turn.role === "user" ? (
                <div className="rounded-lg bg-brand/10 px-4 py-2 text-sm">
                  {turn.content}
                </div>
              ) : (
                <Card>
                  <CardContent className="pt-4">
                    <MarkdownView source={turn.content} />
                    {turn.citations && turn.citations.length > 0 ? (
                      <div className="mt-4 border-t pt-3">
                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                          Sources retrieved for this answer
                        </p>
                        <div className="flex flex-col gap-1.5">
                          {turn.citations.map((c, ci) => (
                            <HitCard
                              key={`${c.repo}/${c.file_path}-${ci}`}
                              hit={c}
                              index={ci + 1}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {turn.model ? (
                      <p className="mt-3 text-xs text-muted-foreground">{turn.model}</p>
                    ) : null}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        ))}

        {chat.isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Retrieving code and composing an answer…
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      {chat.error ? (
        <Alert variant="danger">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Couldn&rsquo;t answer</AlertTitle>
          <AlertDescription>
            {chat.error instanceof Error ? chat.error.message : String(chat.error)}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex items-end gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter newlines — the convention everywhere else.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="How does the revenue waterfall classify expansion?"
          rows={2}
          className="flex-1 resize-none"
          disabled={chat.isPending}
        />
        <Button onClick={send} disabled={chat.isPending || !draft.trim()}>
          <CornerDownLeft className="mr-2 h-4 w-4" />
          Ask
        </Button>
      </div>
    </div>
  );
}
